import React from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Share2, Wrench } from "lucide-react";
import { useMemorial } from "@/contexts/MemorialContext";
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";

interface MemorialHeaderProps {
  isPreview: boolean;
  onBackToAdmin: () => void;
  showFixHeadstone?: boolean;
  onReportIssue?: () => void;
}

export const MemorialHeader: React.FC<MemorialHeaderProps> = ({ 
  isPreview, 
  onBackToAdmin,
  showFixHeadstone = false,
  onReportIssue,
}) => {
  const location = useLocation();
  const { personalDetails } = useMemorial();
  const hasPreviewInUrl = location.search.includes('preview=true');
  
  // Show Fix Headstone button only if company offers repairs and not in preview
  const showReportIssue = showFixHeadstone && !hasPreviewInUrl;

  const handleShareMemorial = async () => {
    // Get current URL and remove any preview parameter
    const currentUrl = window.location.href;
    const cleanUrl = currentUrl.replace(/[?&]preview=true/, '');
    
    // Create dynamic title with person's name(s)
    const name = buildMemorialDisplayName({
      first_name: personalDetails.firstName,
      last_name: personalDetails.lastName,
      person2_first_name: personalDetails.person2FirstName,
      person2_last_name: personalDetails.person2LastName,
    });
    const shareTitle = name && name !== 'Memorial'
      ? `${name} — Memorial on Pontis`
      : 'Memorial Page on Pontis';
    
    // Only use the Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: cleanUrl
        });
      } catch (error) {
        // Only log error, don't show toast or attempt clipboard copy
        console.error('Error sharing:', error);
      }
    }
  };

  const handleLogoClick = () => {
    window.location.href = "https://pontis.life/";
  };


  return (
    <header className="sticky top-0 z-50 bg-brand-primary text-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 py-2 relative flex justify-center items-center">
        {/* Left side: Back button (preview mode only) */}
        {hasPreviewInUrl && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBackToAdmin}
            className="absolute left-4 text-white hover:text-white hover:border-white hover:border hover:bg-transparent text-xs px-2"
          >
            Back
          </Button>
        )}
        
        {/* Left side: Report Issue button (B2B memorials, not in preview) */}
        {showReportIssue && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onReportIssue}
            className="absolute left-2 text-white hover:text-white hover:border-white hover:border hover:bg-transparent text-xs px-1.5"
          >
            <Wrench className="w-3 h-3 mr-1" />
            Fix Headstone
          </Button>
        )}
        
        <div>
          <img 
            src="/lovable-uploads/859fa68d-a569-44b3-8316-bfe14dd34acb.png" 
            alt="Pontis Logo" 
            className="h-7 w-auto"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer', filter: 'brightness(0) invert(1)' }}
          />
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleShareMemorial}
          className="absolute right-2 text-white hover:text-white hover:border-white hover:border hover:bg-transparent text-xs px-1.5"
          aria-label="Share memorial"
        >
          <Share2 className="w-3 h-3 mr-1" />
          Share
        </Button>
      </div>
    </header>
  );
};
